import type {
  GetComponentDetailsReturnType,
  FocusComponentInViewProps
} from "../useStudioAPI.types";
import { StudioNodeType } from "../useStudioAPI.types";
import type { Node as ReactFlowNode } from "../../../components/ProcessEditor/utils/createNodes";
import {
  getReactFlowNodePositon,
  getNodeDetailsFromReactFlowNode
} from "../utils/studioFEAPI.studio2.utils";
import {
  generateNodeData,
  getWebGLNodeId
} from "../utils/studioFEAPI.webGL.utils";
import { useStudio2ComponentInView } from "../hooks/useStudio2ComponentInView/useStudio2ComponentInView";
import { useReactFlow } from "react-flow-renderer";
import {
  ComponentType,
  getComponentIdFromWebGLNodeId,
  getComponentType,
  getElementParentIds,
  getWebGLNodeTypeToComponentType
} from "@certa/process-designer";
import { getComponentLocation } from "@certa/process-designer/src/hooks/useComponentLocation";
import { NodeTypes as ReactFlowNodeTypes } from "../../../components/ProcessEditor/types";
import { getElementByKey } from "@certa/process-designer/src/engines/renderingEngine/renderer";
import { useSafeHook } from "../hooks/useSafeHook";
import { useIsWebGLStudio } from "../hooks/useIsStudioWebGL";
import { ComponentTypeToNodeType } from "../useStudioAPI.constants";
import debounce from "lodash-es/debounce";
import { ComponentKey } from "@certa/studio-core";
import { useStudioAPIStore } from "../store";
import { adjustViewportOnNodeSelection } from "@certa/process-designer/src/pixi-components/layers/viewport/viewportPanControl";
import { studioState } from "../state";
import { isDev } from "@certa/common/utils/isDev";
import { studioDesignerURLRoutes } from "@certa/studio-common/src/utils";
import { useIsStudioPreview } from "./useIsStudioPreview";
import { useProcessDetailsStore } from "@certa/workflow-preview/store";
import { focusPreviewComponentInView } from "@certa/workflow-preview/utils";
import { getPreviewIdFromTag } from "../../../../../workflowPreview/src/utils/transformMetaJSONToPlatformAPIData";
import { useStudioURLParams } from "@certa/studio-common/src/hooks";

// TODO: To be removed, need better way to handle animations (https://thevetted.atlassian.net/browse/DS-5380)
const debounceAdjustViewportOnNodeSelection = debounce(
  adjustViewportOnNodeSelection,
  100
);

const debouncedFocusPreviewComponentInView = debounce(
  focusPreviewComponentInView,
  100
);

export const useStudioCanvasActions = () => {
  const isWebGLStudio = useIsWebGLStudio();
  const isStudioPreview = useIsStudioPreview();
  const { processId: processIDFromURL } = useStudioURLParams();
  const processId = processIDFromURL.toString();

  const { getNodes, setNodes } = useSafeHook(useReactFlow)();
  const { setComponentInView, expandParents: expandParentsStudioRegular } =
    useSafeHook(useStudio2ComponentInView)();
  const setCollapsedNodes = useStudioAPIStore(state => state.setCollapsedNodes);
  const setSelectedWebGLNodeID = (webglNodeId: string, componentId: string) =>
    studioDesignerURLRoutes.setSelectedComponent({
      nodeId: webglNodeId,
      componentId,
      subview: ""
    });

  const getComponentDetails = ({
    componentId,
    includeAncestors,
    includePosition
  }: {
    componentId?: string;
    includeAncestors?: boolean;
    includePosition?: boolean;
  }): GetComponentDetailsReturnType => {
    const data: GetComponentDetailsReturnType = {
      componentAncestorsData: undefined,
      componentData: undefined
    };

    if (!componentId) return data;

    if (isWebGLStudio) {
      // WebGL Implementation
      const componentType = getComponentType(componentId);
      const processData = studioState().getProcessDef(processId);

      const element = getElementByKey(componentId);
      if (!element) return data;
      const id = getComponentIdFromWebGLNodeId(element.props.key);

      const componentData = generateNodeData(id, componentType, processData);
      if (!componentData) return data;

      data.componentData = {
        type: getWebGLNodeTypeToComponentType(componentType) as StudioNodeType,
        id: componentId,
        tag: componentData.tag,
        data: componentData
      };

      if (includePosition) {
        const position = getComponentLocation(componentId);
        data.componentData.position = position || undefined;
      }

      if (includeAncestors) {
        if (componentType === ComponentType.FIELD) {
          const { Step: webGLStepNodeId, Swimlane: webGLSwimlaneNodeId } =
            getElementParentIds(componentId);

          if (!webGLStepNodeId || !webGLSwimlaneNodeId) return data;

          const stepId = getComponentIdFromWebGLNodeId(webGLStepNodeId);

          const stepData = processData.steps[stepId];

          const swimlaneId = getComponentIdFromWebGLNodeId(webGLSwimlaneNodeId);
          const swimlaneData = processData.swimlanes[swimlaneId];

          if (stepData && swimlaneData) {
            data.componentAncestorsData = {
              [StudioNodeType.STEP]: {
                type: StudioNodeType.STEP,
                id: webGLStepNodeId,
                tag: stepData.tag,
                data: generateNodeData(stepId, ComponentType.STEP, processData)
              },
              [StudioNodeType.SWIMLANE]: {
                type: StudioNodeType.SWIMLANE,
                id: webGLSwimlaneNodeId,
                tag: swimlaneData.tag,
                data: generateNodeData(
                  swimlaneId,
                  ComponentType.SWIMLANE,
                  processData
                )
              }
            };
          }
        }
        if (componentType === ComponentType.STEP) {
          const { Swimlane: webGLSwimlaneNodeId } =
            getElementParentIds(componentId);

          if (!webGLSwimlaneNodeId) return data;

          const swimlaneId = getComponentIdFromWebGLNodeId(webGLSwimlaneNodeId);
          const swimlaneData = processData.swimlanes[swimlaneId];

          if (swimlaneData) {
            data.componentAncestorsData = {
              [StudioNodeType.SWIMLANE]: {
                type: StudioNodeType.SWIMLANE,
                id: webGLSwimlaneNodeId,
                tag: swimlaneData.tag,
                data: generateNodeData(
                  swimlaneId,
                  ComponentType.SWIMLANE,
                  processData
                )
              }
            };
          }
        }
      }
    } else {
      // Studio 2.0 Implementation

      // This ideally should never happen, due to the isWebGLStudio check, but this is encountered in Sentry
      // We were unable to reproduce this, so adding this check
      // For dev mode, we are throwing error if it gets caught
      if (typeof getNodes !== "function") {
        if (isDev)
          throw new Error("getNodes is not available from useReactFlow");

        return {};
      }

      const allNodes = getNodes() as ReactFlowNode[];
      const node = allNodes.find(node => node.id === componentId);

      if (!node) return data;

      data.componentData = getNodeDetailsFromReactFlowNode(node);

      if (includeAncestors) {
        // If node's type is step, then it's parent is a swimlane
        if (node.type === ReactFlowNodeTypes.STEP) {
          const parentNodeOfStep = allNodes.find(n => node.parentNode === n.id);

          if (parentNodeOfStep) {
            data.componentAncestorsData = {
              [StudioNodeType.SWIMLANE]:
                getNodeDetailsFromReactFlowNode(parentNodeOfStep)
            };
          }
        }

        // If node's type is field, then we find it's parent (step) and grandparent (swimlane)
        if (node.type === ReactFlowNodeTypes.FIELD) {
          const parentNodeOfField = allNodes.find(
            n => node.parentNode === n.id
          );
          const parentNodeOfStep = allNodes.find(
            n => parentNodeOfField?.parentNode === n.id
          );
          if (parentNodeOfField && parentNodeOfStep) {
            data.componentAncestorsData = {
              [StudioNodeType.SWIMLANE]:
                getNodeDetailsFromReactFlowNode(parentNodeOfStep),
              [StudioNodeType.STEP]:
                getNodeDetailsFromReactFlowNode(parentNodeOfField)
            };
          }
        }
      }

      if (includePosition) {
        const nodePosition = getReactFlowNodePositon(node);
        data.componentData.position = nodePosition || undefined;
      }
    }

    return data;
  };

  const getComponentIdFromComponentTag = ({
    componentTag,
    componentType
  }: {
    componentTag: string;
    componentType:
      | ComponentKey.SWIMLANES
      | ComponentKey.STEPS
      | ComponentKey.FIELDS;
  }) => {
    const component = studioState().getComponentByTag({
      processId,
      componentKey: componentType,
      componentTag
    });
    if (component) {
      return component.id;
    } else {
      // eslint-disable-next-line no-console
      console.error(
        "Cannot focus on component as component not found with componentTag :",
        componentTag
      );
    }
  };

  /**
   * Pans canvas to bring the specified component to center of viewport and resets the zoom to 100%
   * @param props.config - Takes optional from the centre of the screen, these offset values are subtracted from the centre point values
   * i.e Passing a positive xOffset make the centre of the screen to the right of the in focus component and a positive yOffset makes the centre of the screen below the in focus component
   */
  const focusComponentInView = ({
    componentType,
    config,
    ...props
  }: FocusComponentInViewProps) => {
    let componentId: string;

    if ("componentId" in props) {
      componentId = props.componentId;
    } else {
      componentId =
        getComponentIdFromComponentTag({
          componentTag: props.componentTag,
          componentType
        }) || "";
    }

    if (!componentId) return;

    if (isWebGLStudio && !isStudioPreview) {
      expandParentsNodesInCanvas({
        componentId,
        componentType
      });
      const webglNodeId =
        getWebGLNodeId(componentId, ComponentTypeToNodeType[componentType]) ||
        "";
      // adding a delay in adjusting the viewport after layout calculation is done which got triggered by setCollapsedNodes.
      // TODO- this is a temporary fix, need to find a better way to handle this
      debounceAdjustViewportOnNodeSelection(webglNodeId, config);
    } else if (isStudioPreview) {
      // Expand Parents (if any)
      expandParentsNodesInCanvas({
        componentId,
        componentType
      });

      debouncedFocusPreviewComponentInView({
        processId,
        componentType,
        componentId
      });

      // Selecting the step in the sidebar
      // Ideally this is not a part of focusing the component, but doing this to have consistent API
      if (componentType === ComponentKey.STEPS) {
        const stepTag = studioState().getStep(processId, componentId)?.tag;
        if (!stepTag) {
          return;
        }
        useProcessDetailsStore.getState().setSelectedStepByTag(stepTag);
      } else if (componentType === ComponentKey.FIELDS) {
        const parentStepTag: string | undefined =
          studioState().getStepByFieldId(processId, componentId)?.tag;
        if (!parentStepTag) {
          return;
        }
        useProcessDetailsStore.getState().setSelectedStepByTag(parentStepTag);
      }
    } else {
      setComponentInView(componentId, config);
    }
  };

  const selectComponent = ({
    componentTag,
    componentType
  }: {
    componentTag: string;
    componentType:
      | ComponentKey.SWIMLANES
      | ComponentKey.STEPS
      | ComponentKey.FIELDS;
  }) => {
    const componentId =
      getComponentIdFromComponentTag({
        componentTag,
        componentType
      }) || "";

    expandParentsNodesInCanvas({
      componentId,
      componentType
    });

    if (isWebGLStudio) {
      const webglNodeId = getWebGLNodeId(
        componentId,
        ComponentTypeToNodeType[componentType]
      );

      if (webglNodeId) {
        setSelectedWebGLNodeID(webglNodeId, componentId);
      }
    } else {
      // Studio 2.0 Implementation

      // Timeout of 10s added so getNodes() gets the updated nodes after the expansion of parent nodes are complete
      setTimeout(() => {
        const allNodes = getNodes() as ReactFlowNode[];
        setNodes(
          allNodes.map(n => ({ ...n, selected: n.uid === componentId }))
        );
      }, 10);
    }
  };

  const unselectSelectedComponent = () => {
    if (isWebGLStudio) {
      // WebGL Implementation
      setSelectedWebGLNodeID("", "");
    } else {
      // Studio 2.0 Implementation
      // Timeout of 10s added so getNodes() gets the updated nodes after the expansion of parent nodes are complete.
      setTimeout(() => {
        const allNodes = getNodes() as ReactFlowNode[];
        setNodes(allNodes.map(n => ({ ...n, selected: false })));
      }, 10);
    }
  };

  const expandParentsNodesInCanvas = ({
    componentId,
    componentType
  }: {
    componentId: string;
    componentType:
      | ComponentKey.SWIMLANES
      | ComponentKey.STEPS
      | ComponentKey.FIELDS;
  }) => {
    if (isWebGLStudio && !isStudioPreview) {
      const { componentData, componentAncestorsData } = getComponentDetails({
        componentId: getWebGLNodeId(
          componentId,
          ComponentTypeToNodeType[componentType]
        ),
        includeAncestors: true
      });

      if (!componentData) return;

      if (
        componentData.type === StudioNodeType.STEP ||
        componentData.type === StudioNodeType.FIELD
      ) {
        if (!componentAncestorsData) return;

        const parentSwimlaneId =
          componentAncestorsData[StudioNodeType.SWIMLANE]?.id;
        const parentStepId = componentAncestorsData[StudioNodeType.STEP]?.id;

        if (parentSwimlaneId) {
          setCollapsedNodes(prev => prev.filter(id => id !== parentSwimlaneId));
        }
        if (parentStepId) {
          setCollapsedNodes(prev => prev.filter(id => id !== parentStepId));
        }
      }
    } else if (isStudioPreview) {
      // Studio Preview Implementation
      const { componentAncestorsData } = getComponentDetails({
        componentId: getWebGLNodeId(
          componentId,
          ComponentTypeToNodeType[componentType]
        ),
        includeAncestors: true
      });

      if (!componentAncestorsData) return;

      // We don't need to expand anything for swimlanes
      if (componentType === ComponentKey.SWIMLANES) return;

      const { setExpandedSwimlanes } = useProcessDetailsStore.getState();

      if (componentType === ComponentKey.STEPS) {
        const parentSwimlaneTag =
          componentAncestorsData[StudioNodeType.SWIMLANE]?.data.tag;
        if (!parentSwimlaneTag) return;

        const swimlanePreviewId = getPreviewIdFromTag(parentSwimlaneTag);
        if (!swimlanePreviewId) return;

        setExpandedSwimlanes(prev => [...prev, swimlanePreviewId]);
      } else if (componentType === ComponentKey.FIELDS) {
        const grandParentSwimlaneTag =
          componentAncestorsData[StudioNodeType.SWIMLANE]?.data.tag;
        if (!grandParentSwimlaneTag) return;

        const swimlanePreviewId = getPreviewIdFromTag(grandParentSwimlaneTag);
        if (!swimlanePreviewId) return;

        setExpandedSwimlanes(prev => [...prev, swimlanePreviewId]);
      }
    } else {
      // Studio 2.0 implementation
      expandParentsStudioRegular(componentId);
    }
  };

  const expandParentComponents = ({
    componentTag,
    componentType
  }: {
    processId: string;
    componentTag: string;
    componentType:
      | ComponentKey.SWIMLANES
      | ComponentKey.STEPS
      | ComponentKey.FIELDS;
  }) => {
    const componentId = getComponentIdFromComponentTag({
      componentTag,
      componentType
    });

    if (!componentId) return;

    expandParentsNodesInCanvas({
      componentId,
      componentType
    });
  };

  // Note: Do not add export more functions here,
  // Will be deprecated soon when we deprecate Studio 2.0
  return {
    focusComponentInView,
    getComponentDetails,
    selectComponent,
    unselectSelectedComponent,
    expandParentComponents
  };
};
